import { Fragment, Suspense, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabase';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import HomeRedesign from './pages/HomeRedesign';
import SearchResults from './pages/SearchResults';
import { isRedesign } from './lib/redesign';
import CasesEntry from './redesign/CasesEntry';
import CasesListEntry from './redesign/CasesListEntry';
import CaseRouteEntry from './redesign/CaseRouteEntry';
import AgentGate from './redesign/AgentGate';
import { CasesFilterProvider } from './redesign/casesFilterContext';
import ReportsEntry from './redesign/reports/ReportsEntry';
import { lazyNamed, lazyWithRetry, PageErrorBoundary } from './lib/lazyPage';
import { onAuthEvent, syncCacheUser } from './lib/cacheLifecycle';

import { VoiceModalProvider } from './components/VoiceModal';
import { ComingSoonProvider } from './components/ComingSoon';
import { UploadProvider } from './components/UploadProvider';
import { DraftPOProvider } from './components/DraftPOFlow';
import { OrgProvider, useOrg } from './components/OrgContext';
import { LanguageProvider } from './i18n';
import { C } from './theme';

// Every page except the first screens is loaded only when it is first opened, so a first visit downloads what it
// needs and not the whole app (see lib/lazyPage.tsx for what happens if a deploy removes a file mid-session).
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Cases = lazyWithRetry(() => import('./pages/Cases'));
const CasesHub = lazyWithRetry(() => import('./pages/CasesHub'));
const CasesList = lazyWithRetry(() => import('./pages/CasesList'));
const NewToday = lazyWithRetry(() => import('./pages/NewToday'));
const CasePage = lazyWithRetry(() => import('./pages/CasePage'));
const History = lazyWithRetry(() => import('./pages/History'));
const MatchQueue = lazyWithRetry(() => import('./pages/MatchQueue'));
const CaseFocus = lazyWithRetry(() => import('./pages/CaseFocus'));
const DocumentView = lazyWithRetry(() => import('./pages/DocumentView'));
const DiscrepancyTypesView = lazyWithRetry(() => import('./redesign/DiscrepancyTypesView'));
const SettingsPageView = lazyWithRetry(() => import('./redesign/SettingsPageView'));
const UploadPageView = lazyWithRetry(() => import('./redesign/UploadPageView'));
const DocumentPageView = lazyWithRetry(() => import('./redesign/DocumentPageView'));
const SupplierDetail = lazyWithRetry(() => import('./pages/SupplierDetail'));
const Inventory = lazyWithRetry(() => import('./pages/Inventory'));
const Schedule = lazyWithRetry(() => import('./pages/Schedule'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Commissions = lazyWithRetry(() => import('./pages/Commissions'));
const CommissionRules = lazyWithRetry(() => import('./pages/CommissionRules'));
const MarginDetail = lazyNamed(() => import('./pages/DistributorReports'), 'MarginDetail');
const PriceRisesDetail = lazyNamed(() => import('./pages/DistributorReports'), 'PriceRisesDetail');
const ReportsHub = lazyWithRetry(() => import('./pages/ReportsHub'));
const ForecastDetail = lazyNamed(() => import('./pages/ReportsHub'), 'ForecastDetail');
const CommissionsDetail = lazyNamed(() => import('./pages/ReportsHub'), 'CommissionsDetail');
const PerformanceDetail = lazyNamed(() => import('./pages/ReportsHub'), 'PerformanceDetail');
const DiscrepanciesDetail = lazyNamed(() => import('./pages/ReportsHub'), 'DiscrepanciesDetail');
const RevenueDetail = lazyNamed(() => import('./pages/ReportsHub'), 'RevenueDetail');
const PayablesDetail = lazyNamed(() => import('./pages/ReportsHub'), 'PayablesDetail');
const OperationsDetail = lazyNamed(() => import('./pages/ReportsHub'), 'OperationsDetail');
const ExceptionsDetail = lazyNamed(() => import('./pages/ReportsHub'), 'ExceptionsDetail');
const Admin = lazyWithRetry(() => import('./pages/Admin'));
const AdminCosts = lazyWithRetry(() => import('./pages/AdminCosts'));
const ImportData = lazyWithRetry(() => import('./pages/ImportData'));
const FailedDocuments = lazyWithRetry(() => import('./pages/FailedDocuments'));
// Gate /admin to Floxir super-admins; everyone else bounces to Home.
function RequireSuperAdmin({ children }: { children: ReactElement }) {
  const { isSuperAdmin } = useOrg();
  return isSuperAdmin ? children : <Navigate to="/" replace />;
}

// Gate a route to people who hold a permission in the active company (staff via their
// platform role). Everyone else bounces to Home.
function RequirePermission({ permission, children }: { permission: string; children: ReactElement }) {
  const { can } = useOrg();
  return can(permission) ? children : <Navigate to="/" replace />;
}

// The pages of the signed-in app. Keyed by company: switching company remounts every page from scratch, so
// nothing in a page's state (or its remembered data) can carry over from the previous company, not even for a moment.
function PageHost({ children }: { children: ReactNode }) {
  const { orgId } = useOrg();
  return (
    <Fragment key={orgId ?? 'none'}>
      <PageErrorBoundary>
        <Suspense
          fallback={
            <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: C.textMuted }}>
              Loading…
            </div>
          }
        >
          {children}
        </Suspense>
      </PageErrorBoundary>
    </Fragment>
  );
}

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      syncCacheUser(session);   // the page cache must know who is signed in before any page asks it for data
      setSession(session);
      setLoading(false);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      onAuthEvent(event, session);   // a sign-out (or a different user) wipes everything remembered in memory
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          color: C.textMuted,
          background: C.bg,
        }}
      >
        Loading...
      </div>
    );
  }

  // Invited users land here with a token in the URL — show the set-password
  // screen regardless of session state so the token can be processed.
  if (window.location.pathname === '/set-password') return <SetPassword />;
  // Password-reset recovery link lands here with a token — same deal: render the
  // reset screen regardless of session so the token can be consumed and the new
  // password set (a recovery link establishes a session, which would otherwise
  // drop the user straight into the app past the reset form).
  if (window.location.pathname === '/reset-password') return <ResetPassword />;

  if (!session) return <Login />;

  return (
    <BrowserRouter>
      <LanguageProvider>
      <OrgProvider>
      <CasesFilterProvider>
      <VoiceModalProvider>
        <ComingSoonProvider>
        <DraftPOProvider>
        <UploadProvider>
        <PageHost>
        <Routes>
          <Route path="/" element={isRedesign() ? <HomeRedesign /> : <Home />} />
          {/* Search results (redesign step 2): only where the release switch is on. */}
          <Route path="/search" element={isRedesign() ? <SearchResults /> : <Navigate to="/" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {/* The new Cases hub (redesign step 3): only where the release switch is on AND the company is a pure agent; everyone else keeps today's page. */}
          <Route path="/cases" element={isRedesign() ? <CasesEntry hub={<CasesHub />} legacy={<Cases />} /> : <Cases />} />
          {/* The four redesigned Cases lists (step 3, piece 4): only where the release switch is on, and only for pure agents (CasesListEntry); they come before /cases/:id. */}
          {isRedesign() && ['attention', 'open', 'matched', 'done'].map((tile) => (
            <Route key={tile} path={`/cases/${tile}`} element={<CasesListEntry list={<CasesList tile={tile} />} />} />
          ))}
          {/* The "New today" list (step 3, piece 5), opened from a Home row: release switch on, pure agents only; before /cases/:id. */}
          {/* The History page (step 3, piece 6), opened from the History circle on the hub: release switch on, pure agents only; before /cases/:id. */}
          {isRedesign() && <Route path="/cases/types" element={<CasesListEntry list={<DiscrepancyTypesView />} />} />}
          {isRedesign() && <Route path="/cases/history" element={<CasesListEntry list={<History />} />} />}
          {isRedesign() && <Route path="/cases/new-today" element={<Navigate to="/" replace />} />}
          {isRedesign() && <Route path="/cases/new-today/by-type/:type" element={<CasesListEntry list={<NewToday />} />} />}
          {isRedesign() && <Route path="/cases/new-today/:row" element={<CasesListEntry list={<NewToday />} />} />}
          <Route path="/cases/match" element={<MatchQueue />} />
          {/* Real URLs, not React state — each side can be loaded, linked and tested
              directly. These must precede /cases/:id or "buying" reads as a case id. */}
          <Route path="/cases/buying" element={<Cases />} />
          <Route path="/cases/selling" element={<Cases />} />
          {/* An agent case (step 3, piece 7c): the new case pages for a pure agent where the release switch is on; everyone else keeps today's page. */}
          {/* A document that has no case, on a page of its own (opened from History): release switch on, pure agents only. */}
          {isRedesign() && <Route path="/documents/:id" element={<CasesListEntry list={<DocumentPageView single />} />} />}
          <Route path="/cases/:id" element={isRedesign() ? <CaseRouteEntry page={<CasePage />} legacy={<CaseFocus />} /> : <CaseFocus />} />
          <Route path="/cases/:id/document" element={isRedesign() ? <CaseRouteEntry page={<DocumentPageView />} legacy={<DocumentView />} /> : <DocumentView />} />
          <Route path="/suppliers/:id" element={<SupplierDetail />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/schedule" element={<Schedule />} />
          {/* Settings: Leo's page for a pure agent where the release switch is on (AgentGate); everyone else, and "More settings", keep today's page. */}
          <Route path="/settings" element={isRedesign() ? <AgentGate page={<SettingsPageView />} legacy={<Settings />} /> : <Settings />} />
          {isRedesign() && <Route path="/settings/more" element={<Settings />} />}
          {isRedesign() && <Route path="/upload" element={<AgentGate page={<UploadPageView />} legacy={<Navigate to="/cases" replace />} />} />}
          <Route path="/commissions" element={<RequirePermission permission="reports.financial"><Commissions /></RequirePermission>} />
          <Route path="/commissions/rules" element={<CommissionRules />} />
          {/* Reports hub (agent orgs): 4 glanceable cards → focused details; the
              hub. Non-agent orgs get
              the full report at /reports (ReportsHub falls through to it). */}
          <Route path="/reports" element={<RequirePermission permission="reports.financial">{isRedesign() ? <ReportsEntry screen="hub" legacy={<ReportsHub />} /> : <ReportsHub />}</RequirePermission>} />
          <Route path="/reports/forecast" element={<RequirePermission permission="reports.financial">{isRedesign() ? <ReportsEntry screen="forecast" legacy={<ForecastDetail />} /> : <ForecastDetail />}</RequirePermission>} />
          <Route path="/reports/commissions" element={<RequirePermission permission="reports.financial">{isRedesign() ? <ReportsEntry screen="commissions" legacy={<CommissionsDetail />} /> : <CommissionsDetail />}</RequirePermission>} />
          {isRedesign() && <Route path="/reports/suppliers" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="suppliers" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          {isRedesign() && <Route path="/reports/suppliers/:id" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="supplier" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          {isRedesign() && <Route path="/reports/customers" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="customers" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          {isRedesign() && <Route path="/reports/customers/:id" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="customer" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          {isRedesign() && <Route path="/reports/commissions/statement/new" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="statement-upload" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          {isRedesign() && <Route path="/reports/commissions/statement/:id" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="statement-result" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          {isRedesign() && <Route path="/reports/archive" element={<RequirePermission permission="reports.financial"><ReportsEntry screen="archive" legacy={<Navigate to="/reports" replace />} /></RequirePermission>} />}
          <Route path="/reports/performance" element={<RequirePermission permission="reports.financial"><PerformanceDetail /></RequirePermission>} />
          <Route path="/reports/discrepancies" element={<RequirePermission permission="reports.financial">{isRedesign() ? <ReportsEntry screen="discrepancies" legacy={<DiscrepanciesDetail />} /> : <DiscrepanciesDetail />}</RequirePermission>} />
          {/* Distributor hub details — revenue, payables, exceptions, operations. */}
          <Route path="/reports/revenue" element={<RequirePermission permission="reports.financial"><RevenueDetail /></RequirePermission>} />
          <Route path="/reports/payables" element={<RequirePermission permission="reports.financial"><PayablesDetail /></RequirePermission>} />
          <Route path="/reports/exceptions" element={<RequirePermission permission="reports.financial"><ExceptionsDetail /></RequirePermission>} />
          <Route path="/reports/operations" element={<RequirePermission permission="reports.financial"><OperationsDetail /></RequirePermission>} />
          <Route path="/reports/margin" element={<RequirePermission permission="reports.financial"><MarginDetail /></RequirePermission>} />
          <Route path="/reports/prices" element={<RequirePermission permission="reports.financial"><PriceRisesDetail /></RequirePermission>} />
          {/* Manufacturing moved into Inventory as the "Production" section;
              keep the old route working (Settings link) by redirecting there. */}
          <Route path="/manufacturing" element={<Navigate to="/inventory#production" replace />} />
          <Route path="/admin" element={<RequireSuperAdmin><Admin /></RequireSuperAdmin>} />
          {/* Staff cost console. Deliberately absent from AppHeader — the backend
              gate (super-admin + a named allowlist) is the real boundary; this route
              simply never advertises itself in customer-facing navigation. */}
          <Route path="/admin/costs" element={<RequireSuperAdmin><AdminCosts /></RequireSuperAdmin>} />
          <Route path="/import" element={<RequirePermission permission="import.run"><ImportData /></RequirePermission>} />
          <Route path="/documents/failed" element={<RequirePermission permission="import.run"><FailedDocuments /></RequirePermission>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PageHost>
        </UploadProvider>
        </DraftPOProvider>
        </ComingSoonProvider>
      </VoiceModalProvider>
      </CasesFilterProvider>
      </OrgProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
